import React, { useEffect, useMemo, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
// @ts-ignore CSS is loaded by the application bundler.
import "ag-grid-community/styles/ag-grid.css";
// @ts-ignore CSS is loaded by the application bundler.
import "ag-grid-community/styles/ag-theme-alpine.css";
// @ts-ignore CSS is loaded by the application bundler.
import "../../AgGridTheme.css";
import {
  CellStyleModule,
  ClientSideRowModelModule,
  ModuleRegistry,
  PaginationModule,
  RowApiModule,
  RowStyleModule,
} from "ag-grid-community";
import { FaBuilding, FaEnvelope, FaGlobe, FaUser, FaUserTag } from "react-icons/fa";
import SearchBar from "../../../SearchBarTable/SearchBarTable";
import SortHeader from "../../SortingHeader";

// CellStyle for cellStyle, RowStyle for getRowClass, RowApi for redrawRows. The
// app registers AllCommunityModule from the FileCenter page; don't depend on it.
ModuleRegistry.registerModules([
  CellStyleModule,
  ClientSideRowModelModule,
  PaginationModule,
  RowApiModule,
  RowStyleModule,
]);

export type ExplorerMode = "role" | "user";

export interface RoleRow {
  id: number;
  role_name: string;
  domain: string;
}

export interface UserRow {
  id: number;
  name: string;
  email: string;
  company: string;
  entra?: string;
}

interface Props {
  mode: ExplorerMode;
  onModeChange: (mode: ExplorerMode) => void;
  roles: RoleRow[];
  users: UserRow[];
  selectedId: number | null;
  onSelect: (id: number) => void;
}

const headerClass = "p-4 text-left text-base font-bold text-gray-700 bg-gray-200 cursor-pointer";

const col = (field: string, headerName: string, icon: React.ReactNode, minWidth = 140) => ({
  headerName,
  field,
  sortable: true,
  flex: 1,
  minWidth,
  headerComponent: SortHeader,
  headerComponentParams: { displayName: headerName, enableSorting: true, icon },
  headerClass,
});

const ROLE_COLS = [
  col("role_name", "Role", <FaUserTag className="text-gray-600" />, 200),
  col("domain", "Domain", <FaGlobe className="text-gray-600" />, 120),
];
const USER_COLS = [
  col("name", "Name", <FaUser className="text-gray-600" />, 160),
  col("email", "Email", <FaEnvelope className="text-gray-600" />, 200),
  col("company", "Company", <FaBuilding className="text-gray-600" />, 160),
];

const defaultColDef = {
  resizable: true,
  cellStyle: {
    padding: "8px 12px",
    fontSize: "16px",
    fontFamily: "'Poppins', sans-serif",
    color: "#374151",
  },
};

const SEGMENT = "flex items-center gap-2 px-4 py-1.5 rounded-md text-sm font-medium transition-colors duration-300";

/**
 * Left panel of Access Explorer: Role | User toggle, search pill and the list
 * to pick from. One element, so the admin-tab-panel height rule applies to it
 * and the grid takes whatever height is left.
 */
const AccessExplorerList: React.FC<Props> = ({
  mode,
  onModeChange,
  roles,
  users,
  selectedId,
  onSelect,
}) => {
  const [search, setSearch] = useState("");
  const gridRef = useRef<AgGridReact>(null);

  useEffect(() => setSearch(""), [mode]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const all: (RoleRow | UserRow)[] = mode === "role" ? roles : users;
    if (!q) return all;
    // Visible columns only (not the hidden Entra id).
    const fields: string[] =
      mode === "role" ? ["role_name", "domain"] : ["name", "email", "company"];
    return all.filter((r) =>
      fields.some((f) => String((r as any)[f] ?? "").toLowerCase().includes(q)),
    );
  }, [mode, roles, users, search]);

  // getRowClass is only read when a row is drawn.
  useEffect(() => {
    gridRef.current?.api?.redrawRows();
  }, [selectedId]);

  const segment = (m: ExplorerMode, label: string, icon: React.ReactNode) => (
    <button
      type="button"
      aria-pressed={mode === m}
      onClick={() => mode !== m && onModeChange(m)}
      className={`${SEGMENT} ${
        mode === m ? "bg-white text-gray-800 shadow" : "text-gray-600 hover:bg-gray-300"
      }`}
    >
      {icon}
      {label}
    </button>
  );

  return (
    <div className="p-4 bg-white shadow-md rounded-xl font-poppins flex flex-col">
      <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
        {/* Narrower than the other tabs' pill so the toggle fits beside it. */}
        <div className="flex items-center bg-gray-200 px-3 py-2 rounded-full w-64 min-w-0 [&>div]:w-full [&_input]:w-full [&_input]:min-w-0">
          <SearchBar
            searchQuery={search}
            onSearchChange={setSearch}
            placeholder={mode === "role" ? "Search roles..." : "Search users..."}
          />
        </div>
        <div className="flex items-center gap-1 bg-gray-200 rounded-lg p-1" role="group" aria-label="Look up by">
          {segment("role", "Role", <FaUserTag className="w-3.5 h-3.5" />)}
          {segment("user", "User", <FaUser className="w-3.5 h-3.5" />)}
        </div>
      </div>
      <div
        className="ag-theme-alpine font-poppins text-base text-gray-700 flex-1 min-h-[240px]"
        style={{ width: "100%", border: "1px solid #E7E4FF", borderRadius: "8px" }}
      >
        <AgGridReact
          key={mode}
          ref={gridRef}
          theme="legacy"
          rowData={rows}
          columnDefs={mode === "role" ? ROLE_COLS : USER_COLS}
          defaultColDef={defaultColDef}
          domLayout="normal"
          rowHeight={60}
          headerHeight={55}
          pagination={true}
          paginationPageSize={50}
          onRowClicked={(event) => event.data && onSelect(event.data.id)}
          getRowClass={(params) => (params.data?.id === selectedId ? "admin-row-selected" : "")}
        />
      </div>
    </div>
  );
};

export default AccessExplorerList;
